import * as THREE from "three";
import { drawItemIcon } from "../items/itemArt.js";
import { kaponHeightAt } from "./kaponTerrain.js";
import { ROLLING_PAPER_ID, ROLL_BLUNT_MS } from "@ghostland/shared";

/**
 * Client-side BLUNT-ROLLING channel — the visual twin of skinning (`skin.ts`): a pixel progress
 * bar + a rocking rolling-paper icon over the player for ROLL_BLUNT_MS. `start(paperSlot)` begins
 * the channel (from the paper's right-click menu); moving cancels it; when it completes, `tick`
 * returns the paper's slot index so the world sends a `RollBlunt` message.
 */

function makePaperTexture(): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(drawItemIcon(ROLLING_PAPER_ID));
  t.magFilter = THREE.NearestFilter; t.minFilter = THREE.NearestFilter;
  return t;
}

export class RollController {
  private channel: { paperSlot: number; start: number } | null = null;
  private readonly ui = new THREE.Group();
  private readonly barTex: THREE.CanvasTexture;
  private readonly bar: THREE.Sprite;
  private readonly paper: THREE.Sprite;
  private readonly barCanvas: HTMLCanvasElement;

  constructor(private readonly scene: THREE.Scene) {
    this.barCanvas = document.createElement("canvas");
    this.barCanvas.width = 48; this.barCanvas.height = 10;
    this.barTex = new THREE.CanvasTexture(this.barCanvas);
    this.barTex.magFilter = THREE.NearestFilter; this.barTex.minFilter = THREE.NearestFilter;
    this.bar = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.barTex, transparent: true, alphaTest: 0.5, depthWrite: true }));
    this.bar.scale.set(0.5, 0.1, 1); this.bar.position.y = 1.5;
    this.paper = new THREE.Sprite(new THREE.SpriteMaterial({ map: makePaperTexture(), transparent: true, alphaTest: 0.5, depthWrite: true }));
    this.paper.scale.set(0.34, 0.34, 1); this.paper.position.set(0, 1.72, 0);
    this.ui.add(this.bar, this.paper);
    this.ui.visible = false;
    this.scene.add(this.ui);
  }

  isRolling(): boolean { return this.channel != null; }

  start(paperSlot: number, nowMs: number): void {
    this.channel = { paperSlot, start: nowMs };
    this.ui.visible = true;
  }

  cancel(): void { this.channel = null; this.ui.visible = false; }

  /** Per-frame: follow the player, fill the bar, rock the paper. Returns the paper's slot index
   *  when the channel completes (the caller sends `RollBlunt`), else null. */
  tick(playerX: number, playerZ: number, nowMs: number): number | null {
    if (!this.channel) return null;
    this.ui.position.set(playerX, kaponHeightAt(playerX, playerZ), playerZ);
    const t = Math.min(1, (nowMs - this.channel.start) / ROLL_BLUNT_MS);
    this.drawBar(t);
    // Gentle back-and-forth rolling motion.
    this.paper.material.rotation = Math.sin(nowMs / 140) * 0.4;
    if (t >= 1) { const slot = this.channel.paperSlot; this.cancel(); return slot; }
    return null;
  }

  private drawBar(t: number): void {
    const ctx = this.barCanvas.getContext("2d")!;
    const W = this.barCanvas.width, H = this.barCanvas.height;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "#05000a"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#2a1b44"; ctx.fillRect(1, 1, W - 2, H - 2);
    ctx.fillStyle = "#74d86a"; ctx.fillRect(1, 1, Math.round((W - 2) * t), H - 2); // leafy rolling fill
    ctx.fillStyle = "#1a1024";
    ctx.fillRect(0, 0, W, 1); ctx.fillRect(0, H - 1, W, 1); ctx.fillRect(0, 0, 1, H); ctx.fillRect(W - 1, 0, 1, H);
    this.barTex.needsUpdate = true;
  }

  dispose(): void {
    this.scene.remove(this.ui);
    (this.bar.material as THREE.SpriteMaterial).map?.dispose();
    (this.bar.material as THREE.SpriteMaterial).dispose();
    (this.paper.material as THREE.SpriteMaterial).map?.dispose();
    (this.paper.material as THREE.SpriteMaterial).dispose();
  }
}
